import { useTranslation } from 'react-i18next';

/**
 * Every user-facing string rendered by the shared `AddressInput` component
 * (`@klareospace/pharos-lib-web-utils`). Owned here rather than in `pharos-lib-web-utils` so this
 * platform-agnostic hook can build it without creating a front-utils -> web-utils cycle; web-utils
 * imports and re-exports this type for its own consumers.
 */
export interface AddressInputLabels {
  accessInstructions: string;
  accessInstructionsPlaceholder: string;
  addressLine2: string;
  addressLine2Placeholder: string;
  backToSearch: string;
  cantFind: string;
  city: string;
  cityPlaceholder: string;
  coordinates: string;
  country: string;
  digicode: string;
  digicodePlaceholder: string;
  emptyResults: string;
  enterManually: string;
  floor: string;
  floorPlaceholder: string;
  geocode: string;
  geocodeError: string;
  hasElevator: string;
  houseNumber: string;
  houseNumberPlaceholder: string;
  latitude: string;
  longitude: string;
  postalCode: string;
  postalCodePlaceholder: string;
  searchAriaLabel: string;
  searchPlaceholder: string;
  street: string;
  streetPlaceholder: string;
}

export interface UseAddressInputLabelsOptions {
  /** Dot-path prefix under `namespace` where the address label keys live (e.g. `'create.site.address'`). */
  keyPrefix: string;
  /** i18n namespace to read from (e.g. `'tenants'`, `'settings'`). */
  namespace: string;
}

/**
 * Builds the translated `AddressInputLabels` object for the shared `AddressInput` widget. The lib
 * has no i18n access, so every consumer (sites, customers, tenants) supplies its own namespace and
 * key prefix pointing at its `address.*` translation keys.
 */
export function useAddressInputLabels({ keyPrefix, namespace }: UseAddressInputLabelsOptions): AddressInputLabels {
  const { t } = useTranslation(namespace);

  return {
    accessInstructions: t(`${keyPrefix}.accessInstructions`),
    accessInstructionsPlaceholder: t(`${keyPrefix}.accessInstructionsPlaceholder`),
    addressLine2: t(`${keyPrefix}.addressLine2`),
    addressLine2Placeholder: t(`${keyPrefix}.addressLine2Placeholder`),
    backToSearch: t(`${keyPrefix}.backToSearch`),
    cantFind: t(`${keyPrefix}.cantFind`),
    city: t(`${keyPrefix}.city`),
    cityPlaceholder: t(`${keyPrefix}.cityPlaceholder`),
    coordinates: t(`${keyPrefix}.coordinates`),
    country: t(`${keyPrefix}.country`),
    digicode: t(`${keyPrefix}.digicode`),
    digicodePlaceholder: t(`${keyPrefix}.digicodePlaceholder`),
    emptyResults: t(`${keyPrefix}.emptyResults`),
    enterManually: t(`${keyPrefix}.enterManually`),
    floor: t(`${keyPrefix}.floor`),
    floorPlaceholder: t(`${keyPrefix}.floorPlaceholder`),
    geocode: t(`${keyPrefix}.geocode`),
    geocodeError: t(`${keyPrefix}.geocodeError`),
    hasElevator: t(`${keyPrefix}.hasElevator`),
    houseNumber: t(`${keyPrefix}.houseNumber`),
    houseNumberPlaceholder: t(`${keyPrefix}.houseNumberPlaceholder`),
    latitude: t(`${keyPrefix}.latitude`),
    longitude: t(`${keyPrefix}.longitude`),
    postalCode: t(`${keyPrefix}.postalCode`),
    postalCodePlaceholder: t(`${keyPrefix}.postalCodePlaceholder`),
    searchAriaLabel: t(`${keyPrefix}.searchAriaLabel`),
    searchPlaceholder: t(`${keyPrefix}.searchPlaceholder`),
    street: t(`${keyPrefix}.street`),
    streetPlaceholder: t(`${keyPrefix}.streetPlaceholder`),
  };
}
